import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@/components/ui/button";
import type { TransferReportParams } from "@/modules/reports/presentation/api/client";
import { reportsApi } from "@/modules/reports/presentation/api/client";
import { useTransferReportSummaryQuery } from "@/modules/reports/presentation/api/queries";
import { TransferReportsContent } from "@/modules/reports/presentation/ui/transfer/TransferReportsContent";
import {
  TransferReportsFilter,
  type TransferReportsSearch,
} from "@/modules/reports/presentation/ui/transfer/TransferReportsFilter";
import { downloadTransferReportCsv } from "@/modules/reports/presentation/ui/transfer/export-transfer-report-csv";
import { TransferReportsTrendChart } from "@/modules/reports/presentation/ui/transfer/TransferReportsTrendChart";
import { TRANSFER_SECTION_STACK } from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import { TransferReportsSummaryCards } from "@/modules/reports/presentation/ui/transfer/TransferReportsSummaryCards";
import {
  localDayEndISO,
  localDayStartISO,
} from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { FileDownIcon, PackageIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export function TransferReportsPage() {
  const nav = useNavigate({ from: "/app/reports/inventory/transfers" });
  const search = useSearch({
    from: "/app/reports/inventory/transfers",
  }) as TransferReportsSearch;
  const [exporting, setExporting] = useState(false);

  const linesLimit = search.limit ?? 50;
  const linesOffset = search.offset ?? 0;

  const reportParams: TransferReportParams = useMemo(() => {
    const params: TransferReportParams = {
      dateField: search.dateField ?? "submitted",
    };
    if (search.from) {
      params.fromDate = new Date(localDayStartISO(search.from));
    }
    if (search.to) {
      params.toDate = new Date(localDayEndISO(search.to));
    }
    if (search.fromBranchId) {
      params.fromBranchIds = [search.fromBranchId];
    }
    if (search.ingredientCategoryId) {
      params.ingredientCategoryId = search.ingredientCategoryId;
    }
    return params;
  }, [
    search.from,
    search.to,
    search.dateField,
    search.fromBranchId,
    search.ingredientCategoryId,
  ]);

  const summaryQuery = useTransferReportSummaryQuery(reportParams);

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await reportsApi.getTransferReportExport(reportParams);
      const hasData =
        data.byBranch.length > 0 ||
        data.byCategory.length > 0 ||
        data.byItem.length > 0 ||
        data.lines.length > 0;
      if (!hasData) {
        toast.info("ບໍ່ມີຂໍ້ມູນສົ່ງອອກ");
        return;
      }
      downloadTransferReportCsv(data);
      toast.success(
        `ສົ່ງອອກ CSV: ${data.byBranch.length.toLocaleString("lo-LA")} ສາຂາ · ${data.byItem.length.toLocaleString("lo-LA")} ສິນຄ້າ · ${data.lines.length.toLocaleString("lo-LA")}/${data.linesTotal.toLocaleString("lo-LA")} ບັນທັດ`,
      );
    } catch {
      toast.error("ສົ່ງອອກລົ້ມເຫຼວ");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ລາຍງານສາງ", to: "/app/reports/inventory" },
            { label: "ລາຍງານເບິກສະຕ໋ອກ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <PackageIcon className="size-5" />
              ລາຍງານເບິກສະຕ໋ອກ
            </h2>
            <p className="text-muted-foreground">
              ສະຫຼຸບຕາມສາຂາ/ສິນຄ້າ · ແນວໂນ້ມ · CSV ລວມ
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={exporting}
            onClick={() => void handleExport()}
            className="gap-2"
          >
            <FileDownIcon className="size-4" />
            {exporting ? "ກຳລັງສົ່ງອອກ…" : "ສົ່ງອອກ CSV"}
          </Button>
        </div>

        <div className={TRANSFER_SECTION_STACK}>
          <TransferReportsFilter />

          <TransferReportsSummaryCards
            summary={summaryQuery.data}
            isLoading={summaryQuery.isLoading}
          />

          <TransferReportsTrendChart reportParams={reportParams} />

          <TransferReportsContent
          reportParams={reportParams}
          linesOffset={linesOffset}
          linesLimit={linesLimit}
          onLinesPage={(offset) =>
            nav({ search: { ...search, offset } })
          }
          />
        </div>
      </Main>
    </>
  );
}
